import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SkillsPreview = () => {
    const skillCategories = [
      {
        title: "Frontend",
        icon: "Monitor",
        skills: [
          { name: "HTML", icon: "Code2", level: 85, color: "text-orange-500" },
          { name: "CSS", icon: "Box", level: 85, color: "text-blue-500" },
          { name: "Bootstrap", icon: "Layers", level: 75, color: "text-purple-500"},
          { name: "Tailwind CSS", icon: "Layers", level: 80, color: "text-blue-500"},
          { name: "Responsive Design", icon: "Layers", level: 80, color: "text-pink-600" },
          { name: "JavaScript", icon: "FileCode", level: 85, color: "text-yellow-500" },
          { name: "React", icon: "Code", level: 85, color: "text-blue-600" },
          { name: "Vue.js", icon: "Code", level: 70, color: "text-green-500" },
          { name: "Next.js", icon: "Globe", level: 80, color: "text-gray-800" },
          { name: "TypeScript", icon: "FileCode", level: 75, color: "text-blue-500" },
        ]
      },
      {
        title: "Backend",
        icon: "Server",
        skills: [
          { name: "Node.js", icon: "Server", level: 80, color: "text-green-600" },
          { name: "Express.js", icon: "Server", level: 80, color: "text-green-600" },
          { name: "PHP", icon: "Code2", level: 70, color: "text-indigo-600" },
          { name: "EJS", icon:"Code2", level: 75, color: "text-gray-500" },
          { name: "Redis", icon: "Zap", level: 70, color: "text-red-600" },
          { name: "Passport.js", icon: "Shield", level: 75, color: "text-purple-600" },
          { name: "ORM", icon: "Database", level: 80, color: "text-gray-600" },
          { name: "RBAC", icon: "Lock", level: 75, color: "text-red-500" },
        ]
      },
      {
        title: "Databases",
        icon: "Database",
        skills: [
          { name: "MongoDB", icon: "Database", level: 80, color: "text-green-500" },
          { name: "Mongoose", icon: "FileCode", level: 75, color: "text-yellow-500"},
          { name: "MySQL", icon: "Database", level: 80, color: "text-green-500" },
        ]
      },
      {
        title: "DevOps & Tools",
        icon: "Wrench",
        skills: [
          { name: "Docker", icon: "Container", level: 75, color: "text-blue-400" },
          { name: "Git/GitHub", icon: "GitBranch", level: 85, color: "text-gray-800" },
          { name: "Cloud Basics", icon: "Cloud", level: 70, color: "text-blue-500" },
        ]
      },
      {
        title: "CMS & Others",
        icon: "Globe",
        skills: [
          { name: "WordPress", icon: "Globe", level: 80, color: "text-blue-800", description: "(Custom Plugin & Theme Development, Headless CMS)" },
          { name: "CRM", icon: "Users", level: 70, color: "text-orange-500" },
        ]
      }
    ];

  return (
    <section className="pb-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
            <Icon name="Wrench" size={16} className="mr-2" />
            Technical Expertise
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
            Skills & Technologies
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-8">
            Skilled in MERN Stack (MongoDB, Express, React, Node.js) for scalable web apps, WordPress plugin & theme development, headless CMS integration, and cloud deployment. Experienced with CI/CD pipelines, GitHub workflows, and modern DevOps practices.
          </p>
        </div>
        
        {/* Skills Categories */}
        <div className="space-y-12 mb-12">
          {skillCategories.map((category, categoryIndex) => (
            <div key={category.title} className="bg-surface rounded-xl p-8 border border-border">
              {/* Category Header */}
              <div className="flex items-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mr-4">
                  <Icon name={category.icon} size={24} />
                </div>
                <h3 className="text-2xl font-bold text-text-primary">{category.title}</h3>
              </div>
              
              {/* Skills Grid for this Category */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {category.skills.map((skill, skillIndex) => (
                  <div
                    key={skill.name}
                    className="bg-muted/50 rounded-lg p-4 text-center hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-border/50"
                  >
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-muted mb-3 ${skill.color}`}>
                      <Icon name={skill.icon} size={20} />
                    </div>
                    
                    <h4 className="font-semibold text-text-primary mb-2 text-sm">{skill.name}</h4>
                    
                    {skill.description && (
                      <p className="text-xs text-text-secondary mb-2">{skill.description}</p>
                    )}
                    
                    {/* Skill Level Bar */}
                    <div className="w-full bg-muted rounded-full h-1.5 mb-2">
                      <div
                        className="bg-primary h-1.5 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${skill.level}%` }}
                      />
                    </div>
                    
                    <span className="text-xs text-text-secondary">{skill.level}%</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/* CTA */}
        <div className="text-center">
          <Link to="/skills">
            <Button variant="outline" size="lg" iconName="ArrowRight" iconPosition="right" className="w-full sm:w-auto border-2 border-slate-300 dark:border-input">
              View All Skills
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SkillsPreview;